import React, { useState, useEffect } from 'react';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import { supabase } from '../../lib/supabase';
import './CommitteeMembersView.css';

const CommitteeMembersView = ({ tenantId, onBack }) => {
  const [committees, setCommittees] = useState([]);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [formData, setFormData] = useState({
    teacher_id: '',
    committee_name: '',
    member_role: '',
    responsibilities: '',
    notes: ''
  });

  useEffect(() => {
    console.log('CommitteeMembersView mounted with tenantId:', tenantId);
    if (!tenantId) {
      setError('tenantId is required but not provided');
      return;
    }
    loadCommittees();
    loadTeachers();
  }, [tenantId]);

  const loadCommittees = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all unique committee names from teacher_assignments
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('committee_name')
        .eq('tenant_id', tenantId)
        .not('committee_name', 'is', null)
        .not('committee_name', 'eq', '');

      if (assignmentsError) {
        throw new Error(`Failed to fetch committees: ${assignmentsError.message}`);
      }

      // Get unique committee names
      const uniqueCommittees = [...new Set(assignments.map(a => a.committee_name))].sort();
      setCommittees(uniqueCommittees.map(name => ({ name, displayName: name })));

    } catch (err) {
      console.error('Error loading committees:', err);
      setError(`Failed to load committees: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const teachers = await TeacherAssignmentsService.getAllTeachers(tenantId);
      setAvailableTeachers(teachers);
    } catch (err) {
      console.error('Error loading teachers:', err);
      setError(`Failed to load teachers: ${err.message}`);
    } finally {
      setLoadingTeachers(false);
    }
  };

  const handleCommitteeSelect = async (committeeName) => {
    try {
      setLoading(true);
      setError('');
      setSelectedCommittee(committeeName);

      console.log('Loading committee members for:', committeeName);

      // Get assignments for the specific committee
      const { data: assignments, error: assignmentsError } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('committee_name', committeeName);

      if (assignmentsError) {
        throw new Error(`Failed to fetch committee assignments: ${assignmentsError.message}`);
      }

      if (!assignments || assignments.length === 0) {
        setCommitteeMembers([]);
        setMessage(`No members found for ${committeeName}`);
        return;
      }

      // Get teacher information for each assignment
      const teacherIds = [...new Set(assignments.map(a => a.teacher_id))];
      const { data: teachers, error: teachersError } = await supabase
        .from('saas_teachers')
        .select('id, name, code, english_name, department, teacher_no')
        .eq('tenant_id', tenantId)
        .in('id', teacherIds);

      if (teachersError) {
        throw new Error(`Failed to fetch teachers: ${teachersError.message}`);
      }

      // Combine assignments with teacher information
      const members = assignments.map(assignment => {
        const teacher = teachers.find(t => t.id === assignment.teacher_id);
        return {
          ...assignment,
          teacher_name: teacher ? teacher.name : 'Unknown Teacher',
          teacher_english_name: teacher ? teacher.english_name : '',
          teacher_department: teacher ? teacher.department : '',
          teacher_code: teacher ? teacher.code : '',
          teacher_no: teacher ? teacher.teacher_no : null
        };
      });

      setCommitteeMembers(members);
      setMessage(`Loaded ${members.length} members for ${committeeName}`);

    } catch (err) {
      console.error('Error loading committee members:', err);
      setError(`Failed to load committee members: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setFormData({
      teacher_id: '',
      committee_name: selectedCommittee || '',
      member_role: '',
      responsibilities: '',
      notes: ''
    });
    setShowAddMemberModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setFormData({
      teacher_id: member.teacher_id || '',
      committee_name: member.committee_name || '',
      member_role: member.member_role || '',
      responsibilities: member.responsibilities?.join(', ') || '',
      notes: member.notes || ''
    });
    setShowEditMemberModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveMember = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData.teacher_id || !formData.committee_name) {
        setError('Teacher and Committee are required');
        return;
      }

      const selectedTeacher = availableTeachers.find(t => t.id === formData.teacher_id);
      const assignmentData = {
        tenant_id: tenantId,
        teacher_id: formData.teacher_id,
        assignment_type: 'committee_member',
        academic_year: '2025-26',
        term: '1',
        active_from: '2025-09-01',
        active_until: null,
        is_primary: false,
        responsibilities: formData.responsibilities ? formData.responsibilities.split(',').map(r => r.trim()).filter(r => r) : [],
        notes: formData.notes,
        class_id: null,
        subject_name: null,
        eca_id: null,
        committee_name: formData.committee_name,
        duty_name: null,
        duty_type: null,
        duty_location: null,
        teaching_level: null,
        member_role: formData.member_role,
        assignment_data: {
          source: 'manual_entry',
          teacher_code: selectedTeacher ? selectedTeacher.code : '',
          teacher_name: selectedTeacher ? selectedTeacher.name : 'Unknown Teacher',
          committee_name: formData.committee_name
        },
        metadata: {
          created_at: new Date().toISOString(),
          source: 'committee_members_view'
        }
      };

      if (selectedMember) {
        // Update existing member
        const { error } = await supabase
          .from('teacher_assignments')
          .update(assignmentData)
          .eq('id', selectedMember.id);

        if (error) {
          throw new Error(`Failed to update member: ${error.message}`);
        }

        setMessage(`Successfully updated member for ${formData.committee_name}`);
      } else {
        // Add new member
        const { error } = await supabase
          .from('teacher_assignments')
          .insert([assignmentData]);

        if (error) {
          throw new Error(`Failed to add member: ${error.message}`);
        }

        setMessage(`Successfully added member to ${formData.committee_name}`);
      }

      // Reload committee members
      if (selectedCommittee) {
        await handleCommitteeSelect(selectedCommittee);
      }

      setShowAddMemberModal(false);
      setShowEditMemberModal(false);
      setSelectedMember(null);
      setFormData({
        teacher_id: '',
        committee_name: '',
        member_role: '',
        responsibilities: '',
        notes: ''
      });

    } catch (err) {
      setError(`Failed to save member: ${err.message}`);
      console.error('Error saving member:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const { error } = await supabase
        .from('teacher_assignments')
        .delete()
        .eq('id', memberId);

      if (error) {
        throw new Error(`Failed to delete member: ${error.message}`);
      }

      setMessage('Successfully deleted member');
      
      // Reload committee members
      if (selectedCommittee) {
        await handleCommitteeSelect(selectedCommittee);
      }

    } catch (err) {
      setError(`Failed to delete member: ${err.message}`);
      console.error('Error deleting member:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddMemberModal(false);
    setShowEditMemberModal(false);
    setSelectedMember(null);
    setFormData({
      teacher_id: '',
      committee_name: '',
      member_role: '',
      responsibilities: '',
      notes: ''
    });
  };

  // Show error if tenantId is missing
  if (!tenantId) {
    return (
      <div className="committee-members-view">
        <div className="view-header">
          <h3>👥 Committee Members View</h3>
          <p>View and manage committee members</p>
          <button className="back-btn" onClick={onBack}>
            ← Back to Teachers
          </button>
        </div>
        <div className="error-message">
          ❌ tenantId is required but not provided. Please check the configuration.
        </div>
      </div>
    );
  }

  return (
    <div className="committee-members-view">
      <div className="view-header">
        <h3>👥 Committee Members View</h3>
        <p>View and manage committee members</p>
        <button className="back-btn" onClick={onBack}>
          ← Back to Teachers
        </button>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          ✅ {message}
        </div>
      )}

      <div className="view-content">
        {/* Committees List */}
        <div className="committees-list">
          <h4>📋 Available Committees ({committees.length})</h4>
          <div className="committees-grid">
            {committees.map((committee) => (
              <div
                key={committee.name}
                className={`committee-card ${selectedCommittee === committee.name ? 'selected' : ''}`}
                onClick={() => handleCommitteeSelect(committee.name)}
              >
                <div className="committee-info">
                  <h5>{committee.displayName}</h5>
                  <p>Click to view members</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Committee Members Details */}
        {selectedCommittee && (
          <div className="committee-members-details">
            <div className="committee-header">
              <h4>📋 {selectedCommittee}</h4>
              <p>Members and their roles in this committee</p>
              <button className="add-member-btn" onClick={handleAddMember}>
                + Add Member
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading members...</div>
            ) : committeeMembers && committeeMembers.length > 0 ? (
              <div className="committee-members-section">
                <h5>Committee Members ({committeeMembers.length})</h5>
                <div className="committee-members-list">
                  {committeeMembers.map((member, index) => (
                    <div key={index} className="committee-member-item">
                      <div className="member-info">
                        <div className="member-details">
                          <h6>{member.teacher_name || 'Unknown Teacher'}</h6>
                          {member.teacher_english_name && (
                            <p className="member-english">{member.teacher_english_name}</p>
                          )}
                          {member.teacher_department && (
                            <p className="member-dept">{member.teacher_department}</p>
                          )}
                        </div>
                        <div className="member-role-info">
                          {member.member_role && (
                            <span className="role-badge">{member.member_role}</span>
                          )}
                          <span className="committee-badge">{member.committee_name}</span>
                        </div>
                      </div>
                      <div className="member-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditMember(member)}
                          title="Edit Member"
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteMember(member.id)}
                          title="Delete Member"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-members">
                <p>No members found for this committee.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Member Modal */}
      {(showAddMemberModal || showEditMemberModal) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{selectedMember ? 'Edit Committee Member' : 'Add Committee Member'}</h3>
              <button 
                className="close-btn" 
                onClick={handleCancel}
                disabled={loading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Teacher:</label>
                {loadingTeachers ? (
                  <div className="loading-teachers">Loading teachers...</div>
                ) : availableTeachers.length > 0 ? (
                  <select
                    value={formData.teacher_id}
                    onChange={(e) => handleFormChange('teacher_id', e.target.value)}
                    className="form-input"
                    disabled={loading}
                  >
                    <option value="">Select a teacher...</option>
                    {availableTeachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} {teacher.english_name ? `(${teacher.english_name})` : ''} - {teacher.department || 'No Department'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="no-teachers">
                    <span>No teachers available</span>
                    <button onClick={loadTeachers} className="retry-btn" disabled={loadingTeachers}>Retry</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Committee Name:</label>
                <input
                  type="text"
                  value={formData.committee_name}
                  onChange={(e) => handleFormChange('committee_name', e.target.value)}
                  className="form-input"
                  placeholder="Enter committee name"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Member Role:</label>
                <input
                  type="text"
                  value={formData.member_role}
                  onChange={(e) => handleFormChange('member_role', e.target.value)}
                  className="form-input"
                  placeholder="Enter member role (e.g., Chairperson, Secretary)"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Responsibilities:</label>
                <input
                  type="text"
                  value={formData.responsibilities}
                  onChange={(e) => handleFormChange('responsibilities', e.target.value)}
                  className="form-input"
                  placeholder="Enter responsibilities (comma-separated)"
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Notes:</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  className="form-input"
                  placeholder="Enter additional notes"
                  rows="3"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn" 
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={handleSaveMember}
                disabled={loading || !formData.teacher_id || !formData.committee_name}
              >
                {loading ? 'Saving...' : (selectedMember ? 'Update Member' : 'Add Member')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommitteeMembersView;
